import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useGetAllowedCheersQuery,
  useGetCheersQuery,
  usePostCheerMutation,
} from '@/services/socialApi';
import { cn } from '@/lib/cn';
import { ALLOWED_CHEER_EMOJIS } from '@/types/social';

interface CheerPickerProps {
  matchId: string | null | undefined;
  className?: string;
}

const POLL_MS = 4_000;
const COOLDOWN_MS = 1_000;

/**
 * Sticky bottom emoji picker for cheering a live match. Throttled at 1
 * cheer/sec per user per match server-side (see qsocials `postCheer` ->
 * `rate_limited`). We mirror the same throttle in the UI by disabling all
 * buttons for 1s after a tap, and surface 429s as a transient banner.
 *
 * Aggregate count is polled at 4s — Wave 2 will swap this for a
 * Centrifugo subscription on `match:<id>:cheers`.
 */
export function CheerPicker({ matchId, className }: CheerPickerProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();

  const allowedQuery = useGetAllowedCheersQuery();
  const aggregateQuery = useGetCheersQuery(matchId ?? '', {
    skip: !matchId,
    pollingInterval: POLL_MS,
    refetchOnFocus: true,
  });
  const [postCheer] = usePostCheerMutation();

  const [cooldown, setCooldown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cooldown) return;
    const id = window.setTimeout(() => setCooldown(false), COOLDOWN_MS);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(null), 2_000);
    return () => window.clearTimeout(id);
  }, [error]);

  // Server reply lives in `cheer.success` envelope — the unwrap()
  // throws the entire RTK FetchBaseQueryError on non-2xx, including
  // the qsocials `{success:false, code, message}` body.
  const onTap = useCallback(
    async (emoji: string) => {
      if (!matchId || cooldown) return;
      setCooldown(true);
      haptics.impact('medium');
      try {
        await postCheer({ matchId, emoji }).unwrap();
        haptics.notification('success');
      } catch (err) {
        const status = (err as { status?: number }).status;
        const code = (err as { data?: { code?: string } }).data?.code;
        if (status === 429 || code === 'rate_limited' || code === 'cheer_cap') {
          haptics.notification('warning');
          setError(
            code === 'cheer_cap'
              ? t('social.cheer_cap', {
                  defaultValue: "You've cheered enough for this match.",
                })
              : t('social.cheer_rate_limited', {
                  defaultValue: 'Slow down — one cheer per second.',
                }),
          );
        } else {
          haptics.notification('error');
          setError(
            t('social.cheer_error', { defaultValue: 'Cheer failed.' }),
          );
        }
      }
    },
    [matchId, cooldown, haptics, postCheer, t],
  );

  if (!matchId) return null;

  // Fall back to the hand-rolled list if the API hasn't replied yet so the
  // bar appears immediately on mount.
  const emojis = allowedQuery.data ?? ALLOWED_CHEER_EMOJIS;
  const total = aggregateQuery.data?.total ?? 0;
  const byEmoji = aggregateQuery.data?.byEmoji ?? {};

  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 mx-4 mb-2 flex flex-col gap-1 rounded-2xl border border-border bg-bg-secondary/95 p-2 backdrop-blur',
        className,
      )}
    >
      {error && (
        <p className="px-1 text-center text-[11px] font-medium text-warning">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">
          {t('social.cheer_label', { defaultValue: 'Cheer' })}
        </span>
        <span className="text-[11px] font-semibold text-brand">
          {total > 0 ? `🔥 ${total}` : '·'}
        </span>
      </div>

      <div className="flex items-center justify-between gap-1">
        {emojis.map((emoji) => {
          const count = byEmoji[emoji] ?? 0;
          return (
            <button
              key={emoji}
              type="button"
              disabled={cooldown}
              onClick={() => onTap(emoji)}
              className={cn(
                'relative flex h-12 flex-1 items-center justify-center rounded-xl bg-bg-tertiary text-xl',
                'transition-all active:scale-95 active:opacity-80 disabled:opacity-50',
              )}
            >
              <span>{emoji}</span>
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold leading-none text-brand-fg">
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
